import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { Building2, User, Stethoscope, CheckCircle2, XCircle, PenTool } from 'lucide-react';
import DirectorSign from '../../assets/heena_prasad_signature.png';
import { getAvatarUrl } from '../../utils/avatar';

const MoUSignPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mouData, setMouData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchMoU = async () => {
      try {
        const response = await api.get(`/auth/mou/verify/${token}`);
        if (response.data.success) {
          setMouData(response.data.data);
        } else {
          setError('Failed to load MoU. Invalid or expired link.');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Invalid or expired MoU link.');
      } finally {
        setLoading(false);
      }
    };

    fetchMoU();
  }, [token]);

  const handleAgree = async () => {
    setSubmitting(true);
    try {
      const response = await api.post(`/auth/mou/agree`, { token });
      if (response.data.success) {
        setSuccess(true);
        toast.success("MoU Agreed! Signed Agreement and credentials have been sent to your Email & WhatsApp.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit agreement.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisagree = async () => {
    if (!window.confirm('Are you sure you want to disagree? This will reject your registration.')) return;
    
    setSubmitting(true);
    try {
      const response = await api.post(`/auth/mou/disagree`, { token });
      if (response.data.success) {
        setError('You have rejected the MoU. Your registration has been cancelled.');
        toast("MoU Rejected.", { icon: 'ℹ️' });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit disagreement.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-red-100">
          <div className="bg-red-50 p-6 text-center border-b border-red-100">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-900 mb-2">MoU Error</h2>
            <p className="text-red-600">{error}</p>
          </div>
          <div className="p-6 text-center">
            <button 
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-green-100">
          <div className="bg-green-50 p-8 text-center border-b border-green-100">
            <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-green-900 mb-2">Thank You!</h2>
            <p className="text-green-700 text-lg">Your MoU has been successfully signed.</p>
          </div>
          <div className="p-8 text-center">
            <div className="bg-blue-50 text-blue-800 p-4 rounded-xl mb-6 text-left shadow-inner border border-blue-100">
              <p className="font-medium mb-2">We have sent your secure login credentials to:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Your Email Address</li>
                <li>Your WhatsApp Number</li>
              </ul>
            </div>
            <button 
              onClick={() => navigate('/login')}
              className="w-full px-6 py-3 bg-blue-900 text-white rounded-xl hover:bg-blue-800 transition-colors font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 duration-200"
            >
              Go to Login Portal
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getRoleIcon = () => {
    switch (mouData?.role) {
      case 'hospital': return <Building2 className="w-8 h-8 text-white" />;
      case 'doctor': return <Stethoscope className="w-8 h-8 text-white" />;
      default: return <User className="w-8 h-8 text-white" />;
    }
  };

  const getRoleName = () => {
    if (!mouData?.role) return 'Partner';
    return mouData.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  let resolvedTemplate = mouData?.template?.replace(/{dateStr}/g, new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })).replace(/{partnerName}/g, `${mouData?.firstName || ''} ${mouData?.lastName || ''}`.trim()).replace(/{docName}/g, `${mouData?.firstName || ''} ${mouData?.lastName || ''}`.trim()).replace(/{hospitalName}/g, mouData?.hospitalName || 'Hospital').replace(/{regNum}/g, mouData?.regNum || 'N/A').replace(/{address}/g, mouData?.address || 'the registered address');

  if (resolvedTemplate) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(resolvedTemplate, 'text/html');
      const paragraphs = doc.querySelectorAll('p');

      paragraphs.forEach(p => {
        const plainText = p.textContent.trim();
        if (plainText.length < 2 || plainText.length > 200) return;

        const startsWithNumber = /^\d+(\.\d+)*\.?\s+/.test(plainText);
        const isAllUppercase = /^[A-Z0-9\s\.\&\-\(\)]+$/.test(plainText);
        const isShort = plainText.length < 100;

        let isHeading = false;
        if (isAllUppercase && isShort) {
          if (plainText.length >= 15 || startsWithNumber) {
            isHeading = true;
          }
        } else if (startsWithNumber && isShort) {
          const isDate = /january|february|march|april|may|june|july|august|september|october|november|december/i.test(plainText);
          if (!isDate) {
            isHeading = true;
          }
        }

        if (isHeading) {
          const h4 = doc.createElement('h4');
          while (p.firstChild) {
            h4.appendChild(p.firstChild);
          }
          Array.from(p.attributes).forEach(attr => h4.setAttribute(attr.name, attr.value));
          p.parentNode.replaceChild(h4, p);
        }
      });

      resolvedTemplate = doc.body.innerHTML;
    } catch (e) {
      console.error('DOMParser heading extraction failed:', e);
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          
          {/* Header */}
          <div className="bg-blue-900 px-8 py-10 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
            <div className="relative z-10 flex flex-col items-center">
              <div className="bg-white/20 p-4 rounded-full backdrop-blur-sm mb-4 border border-white/30 shadow-lg">
                {getRoleIcon()}
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 drop-shadow-md">
                Memorandum of Understanding
              </h1>
              <p className="text-blue-200 text-lg">
                {getRoleName()} Onboarding Portal
              </p>
            </div>
          </div>

          {/* User Info Bar */}
          <div className="bg-slate-50 border-b border-slate-200 px-8 py-4 flex flex-wrap justify-between items-center text-sm text-slate-600">
            <div>
              <span className="font-semibold text-slate-900">Applicant: </span>
              {mouData?.firstName} {mouData?.lastName}
            </div>
            <div>
              <span className="font-semibold text-slate-900">Email: </span>
              {mouData?.email}
            </div>
          </div>

          {/* Content Body */}
          <div className="px-8 py-10">
            <h2 className="text-2xl font-bold text-slate-800 mb-6 border-b border-slate-200 pb-4">
              {mouData?.templateTitle || `${getRoleName()} Agreement Terms`}
            </h2>
            
            <div className="text-xs text-slate-700 bg-slate-50 p-6 sm:p-8 rounded-xl border border-slate-200 h-[400px] overflow-y-auto shadow-inner mb-6 text-justify space-y-3 [&_p]:text-justify [&_li]:text-justify [&_h4]:font-black [&_h4]:text-sm [&_h4]:text-[#143E78] [&_h4]:mt-4 [&_h4]:mb-1 [&_h5]:font-bold [&_h5]:text-xs [&_h5]:text-slate-800 [&_h5]:mt-3 [&_h5]:mb-1 [&_strong]:font-bold [&_b]:font-black [&_p]:leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:mb-3 [&_li]:mb-1">
              <div dangerouslySetInnerHTML={{ __html: resolvedTemplate }} />
              
              {/* Signatures Section inside the document */}
              <div className="mt-12 pt-8 border-t border-slate-300 flex justify-between items-end">
                <div className="text-center">
                  <div className="h-16 flex items-center justify-center mb-2">
                    <img src={DirectorSign} alt="Director Signature" className="h-16 object-contain" />
                  </div>
                  <div className="w-40 border-t border-slate-400 mx-auto pt-2">
                    <p className="font-bold text-slate-800 text-sm">Heena Prasad</p>
                    <p className="text-xs text-slate-500">Director, DOXEZ</p>
                  </div>
                </div>

                <div className="text-center">
                  <div className="h-16 flex items-center justify-center mb-2">
                    {mouData?.signatureUrl ? (
                      <img src={getAvatarUrl(mouData.signatureUrl)} alt="Applicant Signature" className="h-16 object-contain mix-blend-multiply" />
                    ) : (
                      <div className="text-slate-300 flex items-center justify-center h-full">
                        <PenTool className="w-6 h-6 mr-2 opacity-50" />
                        <span className="text-sm italic">Digital Signature</span>
                      </div>
                    )}
                  </div>
                  <div className="w-40 border-t border-slate-400 mx-auto pt-2">
                    <p className="font-bold text-slate-800 text-sm">{mouData?.firstName} {mouData?.lastName}</p>
                    <p className="text-xs text-slate-500 capitalize">{mouData?.role?.replace('_', ' ')} Applicant</p>
                  </div>
                </div>
              </div>
            </div>

            {mouData?.isAgreed ? (
              <div className="mt-6 bg-emerald-50 border border-emerald-100 rounded-xl p-5 shadow-sm">
                <h3 className="text-emerald-900 font-semibold mb-2 flex items-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mr-2" />
                  Digitally Signed & Accepted
                </h3>
                <p className="text-emerald-800 text-sm">
                  This Memorandum of Understanding has been digitally signed and accepted. Your account is active.
                </p>
              </div>
            ) : (
              <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-5 shadow-sm">
                <h3 className="text-blue-900 font-semibold mb-2 flex items-center">
                  <span className="bg-blue-900 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs mr-2 shadow">i</span>
                  Important Notice
                </h3>
                <p className="text-blue-800 text-sm">
                  By clicking "Agree & Continue", you acknowledge that you have read, understood, and agreed to all the terms and conditions outlined in this Memorandum of Understanding. Once agreed, your account credentials will be automatically generated and sent to you.
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {!mouData?.isAgreed && (
            <div className="bg-slate-50 px-8 py-6 border-t border-slate-200 flex flex-col sm:flex-row gap-4 justify-end">
              <button
                onClick={handleDisagree}
                disabled={submitting}
                className="px-8 py-3 bg-white border-2 border-red-200 text-red-600 rounded-xl hover:bg-red-50 hover:border-red-300 font-semibold transition-all disabled:opacity-50"
              >
                Disagree & Reject
              </button>
              <button
                onClick={handleAgree}
                disabled={submitting}
                className="px-8 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 shadow-lg hover:shadow-green-600/30 font-semibold flex items-center justify-center min-w-[200px] transition-all transform hover:-translate-y-0.5 duration-200 disabled:opacity-50"
              >
                {submitting ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processing...
                  </span>
                ) : (
                  'Agree & Continue'
                )}
              </button>
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default MoUSignPage;
